/**
 * SoundCatalog — the single, typed source of truth for every game sound.
 *
 * Each entry names an intent (DICE_DICE, UI_TAP, …) and carries its mix bus
 * (category → settingsStore volume), a base volume, pool size, loop flag, and
 * optional per-play pitch jitter. URLs are expanded from a single base file
 * name into the format list Howler negotiates over — we ship .webm (Opus) and
 * .m4a (AAC) and let Howler pick the first the device can decode. No platform
 * sniffing (the Phone Dice approach); the browser tells Howler what it can play.
 *
 * Adding a sound: add a SoundId, add a SOUNDS entry, drop `<file>.webm` +
 * `<file>.m4a` under public/assets/sfx/. Triggers reference SoundId, so a typo
 * or a deleted sound is a compile error, not a silent miss.
 *
 * ── Going live ──────────────────────────────────────────────────────────────
 * Until the audio files exist, SOUND_ASSETS_READY stays false: the catalog is
 * never registered/preloaded and every sound.play() is a no-op, so the triggers
 * already wired through the game make zero noise and log zero 404s. Drop the
 * encoded files in, flip SOUND_ASSETS_READY to true, done.
 */

import type { AudioCategory, SoundConfig } from './AudioManager';

/** Flip to true once the .webm/.m4a files exist under SFX_DIR. */
export const SOUND_ASSETS_READY = false;

/** Formats we ship, in Howler negotiation order (best first, universal last). */
const FORMATS = ['webm', 'm4a'] as const;

/** Public dir the encoded sfx live under. BASE_URL keeps it correct under a
 *  sub-path deploy (leading-slash URLs break there — see TSS asset note). */
const SFX_DIR = `${import.meta.env.BASE_URL}assets/sfx/`;

export enum SoundId {
  // ── UI (bus: ui) ──
  UI_TAP = 'UI_TAP',       // default click for any clickable (icon buttons, tabs, div-buttons)
  UI_BUTTON = 'UI_BUTTON', // heftier press for visible, text-labelled buttons
  UI_BACK = 'UI_BACK',
  UI_TOGGLE = 'UI_TOGGLE',
  SHEET_OPEN = 'SHEET_OPEN',
  SHEET_CLOSE = 'SHEET_CLOSE',
  WALLET_OPEN = 'WALLET_OPEN',
  WALLET_CLOSE = 'WALLET_CLOSE',

  // ── Dice / table (bus: sfx) ──
  DICE_PICKUP = 'DICE_PICKUP',
  DICE_THROW = 'DICE_THROW',
  /** Dice-result icon pop-in (the flyout's staggered d-1..6 icons). */
  DICE_RESULT_POP = 'DICE_RESULT_POP',
  /** Dice-result icons landing in a player's PROFILE CARD (distinct from the
   *  flyout pop above). */
  PROFILE_RESULT_POP = 'PROFILE_RESULT_POP',
  // Result-title stingers — one of these plays as the showdown label pops
  // (after the dice icons), picked by outcome kind.
  DICE_RESULT_WIN = 'DICE_RESULT_WIN',     // CEE-LO / trips / natural / point hit
  DICE_RESULT_POINT = 'DICE_RESULT_POINT', // point set (pair / come-out point) + tie
  DICE_RESULT_LOSS = 'DICE_RESULT_LOSS',   // 1-2-3 / craps / seven-out
  /** Dice-vs-dice clatter — reuses Phone Dice's exact sample (great on mobile). */
  DICE_DICE = 'DICE_DICE',
  // Per-face impact (which face of the die struck the surface → its own sound).
  // One shared set serves both floor and wall.
  DICE_FACE_1 = 'DICE_FACE_1',
  DICE_FACE_2 = 'DICE_FACE_2',
  DICE_FACE_3 = 'DICE_FACE_3',
  DICE_FACE_4 = 'DICE_FACE_4',
  DICE_FACE_5 = 'DICE_FACE_5',
  DICE_FACE_6 = 'DICE_FACE_6',
  // First-floor-landing wood thud — one of three samples, picked at random,
  // plays the moment a die first touches the table on a flight (NOT per face,
  // NOT per contact — see Dice.handleCollisionSound). Volume scales with impact.
  DICE_FLOOR_1 = 'DICE_FLOOR_1',
  DICE_FLOOR_2 = 'DICE_FLOOR_2',
  DICE_FLOOR_3 = 'DICE_FLOOR_3',
  // Marble floor-landing variants (5 samples). Currently the active default
  // floor sound — see randomFloorLandSound(). The wood set above is kept so the
  // upcoming scene-editor material property can pick per-surface sounds.
  DICE_FLOOR_MARBLE_1 = 'DICE_FLOOR_MARBLE_1',
  DICE_FLOOR_MARBLE_2 = 'DICE_FLOOR_MARBLE_2',
  DICE_FLOOR_MARBLE_3 = 'DICE_FLOOR_MARBLE_3',
  DICE_FLOOR_MARBLE_4 = 'DICE_FLOOR_MARBLE_4',
  DICE_FLOOR_MARBLE_5 = 'DICE_FLOOR_MARBLE_5',
  // Hand-rattle while shaking held dice — one of three samples, picked at
  // random per shake beat. Volume scales with stroke speed (see DiceController).
  DICE_SHAKE_1 = 'DICE_SHAKE_1',
  DICE_SHAKE_2 = 'DICE_SHAKE_2',
  DICE_SHAKE_3 = 'DICE_SHAKE_3',

  // ── Cash / betting (bus: sfx) ──
  CASH_PLACE = 'CASH_PLACE',
  CASH_PICKUP = 'CASH_PICKUP',
  BET_LOCK = 'BET_LOCK',
  NOTE_GRAB = 'NOTE_GRAB',  // grab the accumulated bill stack (start drag)
  NOTE_PULL = 'NOTE_PULL',  // pull a note from the wallet onto the stack
  NOTE_REMOVE = 'NOTE_REMOVE', // tap a note on the accumulated stack to remove it
  NOTE_SWAP_OPEN = 'NOTE_SWAP_OPEN', // long-press the wallet → radial note-swap menu opens
  NOTE_SLIDE = 'NOTE_SLIDE', // wallet denomination (note index) changes to a different note
  NOTE_DROP = 'NOTE_DROP',  // bills hit the floor of a bet zone
  NOTE_MOVE = 'NOTE_MOVE',  // local stack flies into a bet zone (bet/sidebet placed)
  // Win-flyout beats (user-authored 2026-06-12).
  CASH_LAUNCH = 'CASH_LAUNCH',     // notes thrown / fly out (still pending)
  BET_ICON_POP = 'BET_ICON_POP',   // bet-icon stack pops in (once per flyout)
  CASH_COUNT = 'CASH_COUNT',       // notes start appearing (once per flyout)
  CASH_LAND = 'CASH_LAND',         // cha-ching — +$X label pops at the profile

  // ── Round outcomes (bus: sfx) ──
  COUNTDOWN_TICK = 'COUNTDOWN_TICK',
  COUNTDOWN_GO = 'COUNTDOWN_GO',
  WIN = 'WIN',
  LOSE = 'LOSE',

  // ── Reward reveals (bus: sfx) ──
  REWARD_CHIME = 'REWARD_CHIME',       // tutorial success / congrats modal
  CONFETTI_POP = 'CONFETTI_POP',       // confetti explosion on a dice-unlock reveal
  DICE_REVEAL = 'DICE_REVEAL',         // the die flips in (plays alongside CONFETTI_POP)
  STEP_COMPLETE = 'STEP_COMPLETE',     // tutorial coach-mark step cleared (check pops in)
  /** Gold coin collected — the flyGoldCoin beat when the local player RECEIVES
   *  gold (challenge collect, inbox COLLECT). Owned by goldFly.ts, once per flyout. */
  GOLD_COLLECT = 'GOLD_COLLECT',
  /** Gold coin sent — the flyGoldCoin beat when the local player GIVES gold
   *  (crew daily gift). Same owner, softer chime than a collect. */
  GOLD_SEND = 'GOLD_SEND',

  // ── Bonus suitcase (bus: sfx) ──
  CASE_UNLOCK = 'CASE_UNLOCK',         // a lock flips open
  CASE_LOCKED = 'CASE_LOCKED',         // tapped the lid while still locked (reject shake)
  CASE_OPEN = 'CASE_OPEN',             // lid pops open
  CASE_PRIZE_BIG = 'CASE_PRIZE_BIG',   // high-tier reveal (≥ 20M)
  CASE_PRIZE_SMALL = 'CASE_PRIZE_SMALL', // standard reveal (< 20M)

  // ── Music (bus: music) ──
  MUSIC_LOBBY = 'MUSIC_LOBBY',
  MUSIC_GAME = 'MUSIC_GAME',
}

/**
 * Per-sound authoring config. `file` is the base name (no extension); the rest
 * mirrors SoundConfig minus `src` (derived) plus authoring sugar.
 */
export interface CatalogEntry {
  /** Base file name under SFX_DIR, without extension. */
  file: string;
  category: AudioCategory;
  /** Base volume 0..1 (on top of the bus volume). */
  volume?: number;
  loop?: boolean;
  /** Howler voice pool — raise for rapid-fire sounds (dice clatter). */
  pool?: number;
  /** ± playback-rate jitter applied per play, so repeats don't sound canned. */
  vary?: number;
  /** Override the default FORMATS list for this one sound (e.g. reuse an
   *  existing asset that ships as ogg/m4a instead of webm/m4a). */
  formats?: readonly string[];
  /** Mark true once this sound's files exist. While the global
   *  SOUND_ASSETS_READY is false, only `ready` entries are registered/preloaded
   *  — lets sounds go live incrementally without 404-spamming the rest. */
  ready?: boolean;
}

export const SOUNDS: Record<SoundId, CatalogEntry> = {
  // UI
  // Default UI click — the active GameTheme's `click` role points here. Played
  // for every clickable element by the global delegator (see uiClickSounds.ts).
  // Bigger pool so fast tab/button mashing never clips its own tail.
  [SoundId.UI_TAP]: { file: 'ui/tap', category: 'ui', volume: 0.7, pool: 6, vary: 0.04, ready: true },
  // Press for visible, text-labelled buttons (theme `button` role). Routed by
  // the global delegator; icon buttons + tabs stay on UI_TAP. Light vary so a
  // double-tap doesn't sound canned.
  [SoundId.UI_BUTTON]: { file: 'ui/button', category: 'ui', volume: 0.8, pool: 4, vary: 0.03, ready: true },
  [SoundId.UI_BACK]: { file: 'ui/back', category: 'ui', volume: 0.7 },
  [SoundId.UI_TOGGLE]: { file: 'ui/toggle', category: 'ui', volume: 0.7 },
  [SoundId.SHEET_OPEN]: { file: 'ui/sheet-open', category: 'ui', volume: 0.8 },
  [SoundId.SHEET_CLOSE]: { file: 'ui/sheet-close', category: 'ui', volume: 0.8 },
  // Wallet model slide/unfold in + close — one shared "in-out" whoosh, played at
  // the START of each animation (Wallet.open / Wallet.close).
  [SoundId.WALLET_OPEN]: { file: 'ui/wallet-inout', category: 'ui', volume: 0.9, ready: true },
  [SoundId.WALLET_CLOSE]: { file: 'ui/wallet-inout', category: 'ui', volume: 0.9, ready: true },

  // Dice / table
  [SoundId.DICE_PICKUP]: { file: 'dice/pickup', category: 'sfx', volume: 0.8, pool: 4, vary: 0.05, ready: true },
  // Result-icon pop — fires per icon in the staggered pop-in (3× cee-lo,
  // 2× craps, 1× HI_LO), so pool + jitter keep the burst from sounding canned.
  [SoundId.DICE_RESULT_POP]: { file: 'dice/result-pop', category: 'sfx', volume: 0.9, pool: 4, vary: 0.06, ready: true },
  // Result icons landing in a profile card — one pop per result (not per icon).
  [SoundId.PROFILE_RESULT_POP]: { file: 'dice/profile-pop', category: 'sfx', volume: 0.9, pool: 3, vary: 0.05, ready: true },
  // Result-title stingers — single-voice, no jitter (they're distinct one-shot
  // samples; pitch-warping a musical stinger reads as a glitch).
  [SoundId.DICE_RESULT_WIN]: { file: 'dice/result-win', category: 'sfx', volume: 1, ready: true },
  [SoundId.DICE_RESULT_POINT]: { file: 'dice/result-point', category: 'sfx', volume: 1, ready: true },
  [SoundId.DICE_RESULT_LOSS]: { file: 'dice/result-loss', category: 'sfx', volume: 0.5, ready: true },
  [SoundId.DICE_THROW]: { file: 'dice/throw', category: 'sfx', volume: 0.9, vary: 0.05 },
  // Dice-vs-dice: Phone Dice's exact sample (ships ogg/m4a, so override formats
  // to reuse it as-is). Big pool — a clatter fires many contacts.
  [SoundId.DICE_DICE]: { file: 'dice/dice', category: 'sfx', volume: 0.9, pool: 10, vary: 0.06, formats: ['ogg', 'm4a'], ready: true },
  // Per-face impact, shared by floor + wall (volume comes from impact at play
  // time; no jitter — faces are already distinct samples the artist authored).
  [SoundId.DICE_FACE_1]: { file: 'dice/face-1', category: 'sfx', volume: 1, pool: 4, ready: true },
  [SoundId.DICE_FACE_2]: { file: 'dice/face-2', category: 'sfx', volume: 1, pool: 4, ready: true },
  [SoundId.DICE_FACE_3]: { file: 'dice/face-3', category: 'sfx', volume: 1, pool: 4, ready: true },
  [SoundId.DICE_FACE_4]: { file: 'dice/face-4', category: 'sfx', volume: 1, pool: 4, ready: true },
  [SoundId.DICE_FACE_5]: { file: 'dice/face-5', category: 'sfx', volume: 1, pool: 4, ready: true },
  [SoundId.DICE_FACE_6]: { file: 'dice/face-6', category: 'sfx', volume: 1, pool: 4, ready: true },
  // First-floor-landing wood thud (3 variants). Volume + rate are supplied per
  // play from impact (no catalog vary). Small pool so two near-simultaneous
  // landings don't clip each other's tail.
  [SoundId.DICE_FLOOR_1]: { file: 'dice/floor-1', category: 'sfx', volume: 1, pool: 3, ready: true },
  [SoundId.DICE_FLOOR_2]: { file: 'dice/floor-2', category: 'sfx', volume: 1, pool: 3, ready: true },
  [SoundId.DICE_FLOOR_3]: { file: 'dice/floor-3', category: 'sfx', volume: 1, pool: 3, ready: true },
  // Marble floor-landing (5 variants), the active default. Same per-play
  // volume/rate-from-impact contract as the wood set above.
  [SoundId.DICE_FLOOR_MARBLE_1]: { file: 'dice/floor-marble-01', category: 'sfx', volume: 1, pool: 3, ready: true },
  [SoundId.DICE_FLOOR_MARBLE_2]: { file: 'dice/floor-marble-02', category: 'sfx', volume: 1, pool: 3, ready: true },
  [SoundId.DICE_FLOOR_MARBLE_3]: { file: 'dice/floor-marble-03', category: 'sfx', volume: 1, pool: 3, ready: true },
  [SoundId.DICE_FLOOR_MARBLE_4]: { file: 'dice/floor-marble-04', category: 'sfx', volume: 1, pool: 3, ready: true },
  [SoundId.DICE_FLOOR_MARBLE_5]: { file: 'dice/floor-marble-05', category: 'sfx', volume: 1, pool: 3, ready: true },
  // Hand-rattle (3 variants). Volume comes from stroke speed per play; light
  // vary + bigger pool so a fast rattle of overlapping beats stays organic.
  [SoundId.DICE_SHAKE_1]: { file: 'dice/shake-1', category: 'sfx', volume: 1, pool: 4, vary: 0.05, ready: true },
  [SoundId.DICE_SHAKE_2]: { file: 'dice/shake-2', category: 'sfx', volume: 1, pool: 4, vary: 0.05, ready: true },
  [SoundId.DICE_SHAKE_3]: { file: 'dice/shake-3', category: 'sfx', volume: 1, pool: 4, vary: 0.05, ready: true },

  // Cash / betting
  [SoundId.CASH_PLACE]: { file: 'cash/place', category: 'sfx', volume: 0.9, pool: 4, vary: 0.05 },
  [SoundId.CASH_PICKUP]: { file: 'cash/pickup', category: 'sfx', volume: 0.8, pool: 4, vary: 0.05 },
  [SoundId.BET_LOCK]: { file: 'cash/lock', category: 'sfx', volume: 0.9 },
  // Bill interactions — user-authored, live now.
  [SoundId.NOTE_GRAB]: { file: 'cash/note-grab', category: 'sfx', volume: 0.9, pool: 3, vary: 0.04, ready: true },
  [SoundId.NOTE_PULL]: { file: 'cash/note-pull', category: 'sfx', volume: 0.9, pool: 4, vary: 0.05, ready: true },
  [SoundId.NOTE_REMOVE]: { file: 'cash/note-remove', category: 'sfx', volume: 0.9, pool: 4, vary: 0.05, ready: true },
  [SoundId.NOTE_SWAP_OPEN]: { file: 'cash/note-swap-open', category: 'sfx', volume: 0.9, pool: 2, ready: true },
  [SoundId.NOTE_SLIDE]: { file: 'cash/note-slide', category: 'sfx', volume: 0.9, pool: 4, vary: 0.04, ready: true },
  [SoundId.NOTE_DROP]: { file: 'cash/note-drop', category: 'sfx', volume: 0.9, pool: 6, vary: 0.06, ready: true },
  [SoundId.NOTE_MOVE]: { file: 'cash/notes-move', category: 'sfx', volume: 0.9, pool: 3, vary: 0.04, ready: true },
  // Win-flyout beats. ONE play per flyout for each beat (icon stack /
  // note burst / label cha-ching), not per element — multiple winners'
  // flyouts can overlap, hence the pools.
  [SoundId.CASH_LAUNCH]: { file: 'cash/launch', category: 'sfx', volume: 0.8, pool: 4, vary: 0.04, formats: ['ogg', 'm4a'] },
  [SoundId.BET_ICON_POP]: { file: 'cash/icon-pop', category: 'sfx', volume: 0.9, pool: 3, vary: 0.04, ready: true },
  [SoundId.CASH_COUNT]: { file: 'cash/note-count', category: 'sfx', volume: 0.8, pool: 3, ready: true },
  [SoundId.CASH_LAND]: { file: 'cash/cha-ching', category: 'sfx', volume: 0.9, pool: 4, ready: true },

  // Round outcomes
  [SoundId.COUNTDOWN_TICK]: { file: 'round/tick', category: 'sfx', volume: 0.8 },
  [SoundId.COUNTDOWN_GO]: { file: 'round/go', category: 'sfx', volume: 0.9 },
  [SoundId.WIN]: { file: 'win/ceelo', category: 'sfx', volume: 1, formats: ['ogg', 'm4a'] },
  [SoundId.LOSE]: { file: 'round/lose', category: 'sfx', volume: 0.9 },

  // Reward reveals — user-authored, live now. Musical stingers (chime / reveal)
  // get NO pitch jitter (warping reads as a glitch); each fires once per reveal,
  // so no pool is needed beyond the default.
  [SoundId.REWARD_CHIME]: { file: 'reward/chime', category: 'sfx', volume: 0.9, ready: true },
  [SoundId.CONFETTI_POP]: { file: 'reward/confetti', category: 'sfx', volume: 0.9, ready: true },
  [SoundId.DICE_REVEAL]: { file: 'reward/reveal', category: 'sfx', volume: 1, ready: true },
  [SoundId.STEP_COMPLETE]: { file: 'reward/step-complete', category: 'sfx', volume: 0.9, ready: true },
  // Coin chime — musical stinger, so no pitch jitter. Small pool: rapid
  // COLLECT taps across inbox cells can overlap flyouts.
  [SoundId.GOLD_COLLECT]: { file: 'reward/gold-collect', category: 'sfx', volume: 0.9, pool: 3, ready: true },
  // Gift-send keeps the original coin chime; sends are one-at-a-time so no pool.
  [SoundId.GOLD_SEND]: { file: 'reward/gold-send', category: 'sfx', volume: 0.9, ready: true },

  // Bonus suitcase — user-authored, live now. One-shot reveal stingers get no
  // pitch jitter (warping a musical stinger reads as a glitch); the lock flip
  // can fire twice (two locks), so it gets a small pool.
  [SoundId.CASE_UNLOCK]: { file: 'bonus/case-unlock', category: 'sfx', volume: 0.9, pool: 2, ready: true },
  [SoundId.CASE_LOCKED]: { file: 'bonus/case-locked', category: 'sfx', volume: 0.85, ready: true },
  [SoundId.CASE_OPEN]: { file: 'bonus/case-open', category: 'sfx', volume: 0.9, ready: true },
  [SoundId.CASE_PRIZE_BIG]: { file: 'bonus/case-prize-1', category: 'sfx', volume: 1, ready: true },
  [SoundId.CASE_PRIZE_SMALL]: { file: 'bonus/case-prize-2', category: 'sfx', volume: 1, ready: true },

  // Music
  [SoundId.MUSIC_LOBBY]: { file: 'music/lobby', category: 'music', volume: 0.6, loop: true },
  [SoundId.MUSIC_GAME]: { file: 'music/game', category: 'music', volume: 0.6, loop: true },
};

/** Surface a die can strike (dice-vs-dice handled separately). */
export type DiceSurface = 'floor' | 'wall';

const FACE_SOUNDS = [
  SoundId.DICE_FACE_1, SoundId.DICE_FACE_2, SoundId.DICE_FACE_3,
  SoundId.DICE_FACE_4, SoundId.DICE_FACE_5, SoundId.DICE_FACE_6,
] as const;

/**
 * Resolve the per-face collision sound for a die striking the floor or a wall.
 * `face` is the die value (1-6) of the face that made contact. One shared set
 * serves both surfaces today; `surface` is kept so floor/wall can diverge later
 * without touching call sites.
 */
export function faceCollisionSound(_surface: DiceSurface, face: number): SoundId {
  const idx = Math.min(6, Math.max(1, face)) - 1;
  return FACE_SOUNDS[idx];
}

// Active default floor-landing set for the default scene: marble (5 samples).
// The wood samples (SoundId.DICE_FLOOR_1..3, still registered in SOUNDS) stay
// available for the upcoming scene-editor material property — swap them back in
// here, or select per-surface, once that lands.
const FLOOR_LAND_SOUNDS = [
  SoundId.DICE_FLOOR_MARBLE_1, SoundId.DICE_FLOOR_MARBLE_2, SoundId.DICE_FLOOR_MARBLE_3,
  SoundId.DICE_FLOOR_MARBLE_4, SoundId.DICE_FLOOR_MARBLE_5,
] as const;

/** Pick a random first-floor-landing sample (currently marble). */
export function randomFloorLandSound(): SoundId {
  return FLOOR_LAND_SOUNDS[(Math.random() * FLOOR_LAND_SOUNDS.length) | 0];
}

// Four-way rotation: the three authored shake samples plus the original
// pickup rattle that shake used before (kept in the mix on purpose).
const SHAKE_SOUNDS = [
  SoundId.DICE_SHAKE_1, SoundId.DICE_SHAKE_2, SoundId.DICE_SHAKE_3, SoundId.DICE_PICKUP,
] as const;

/** Pick a random hand-rattle shake sample. */
export function randomShakeSound(): SoundId {
  return SHAKE_SOUNDS[(Math.random() * SHAKE_SOUNDS.length) | 0];
}

/** Build the multi-format src array Howler negotiates over. */
export function srcFor(entry: CatalogEntry): string[] {
  const formats = entry.formats ?? FORMATS;
  return formats.map((ext) => `${SFX_DIR}${entry.file}.${ext}`);
}

/**
 * Catalog as AudioManager.register/preload configs, keyed by SoundId.
 * @param onlyReady when true, include only entries whose files exist (`ready`).
 */
export function buildSoundConfigs(onlyReady = false): Record<string, SoundConfig> {
  const out: Record<string, SoundConfig> = {};
  for (const id of Object.keys(SOUNDS) as SoundId[]) {
    const e = SOUNDS[id];
    if (onlyReady && !e.ready) continue;
    out[id] = {
      src: srcFor(e),
      volume: e.volume,
      loop: e.loop,
      category: e.category,
      pool: e.pool,
      preload: true,
    };
  }
  return out;
}
